import { Box, Card, CardContent, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { resolveLimits } from '@traaker/shared';
import CompetitorMatrixView from '../components/competitors/CompetitorMatrixView';
import HowItWorksCard from '../components/HowItWorksCard';

/**
 * Concurrents — prompts × competitors matrix (3 axes), tracked-competitor
 * management, suggested competitors, and the per-competitor detail popup.
 * All data + interaction lives in CompetitorMatrixView; this page is just the
 * quota gate + header. Quota is the scoping primitive (plan §10.1):
 * competitors===0 ⇒ locked (and the sidebar hides the entry entirely).
 */
export default function Competitors() {
  const { t } = useTranslation();
  const { activeBrand } = useBrand();
  const { tenant } = useAuth();

  const limits = tenant ? resolveLimits(tenant.tier, tenant.settings?.quotaOverrides) : null;
  const competitorQuota = limits?.competitors ?? 0;
  const unlocked = competitorQuota !== 0;
  const brandId = activeBrand?.brandId;

  if (!unlocked) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          {t('competitors.title')}
        </Typography>
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('competitors.locked.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('competitors.locked.body')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} data-tour="page-intro">
        {t('competitors.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('competitors.subtitle')}
      </Typography>

      <HowItWorksCard
        title={t('competitors.howItWorks.title')}
        steps={[
          {
            title: t('competitors.howItWorks.discover.title'),
            description: t('competitors.howItWorks.discover.description'),
            image: '/assets/how-it-works/competitors-discover.webp',
          },
          {
            title: t('competitors.howItWorks.compare.title'),
            description: t('competitors.howItWorks.compare.description'),
            image: '/assets/how-it-works/competitors-compare.webp',
          },
          {
            title: t('competitors.howItWorks.score.title'),
            description: t('competitors.howItWorks.score.description'),
            image: '/assets/how-it-works/competitors-score.webp',
          },
        ]}
      />

      {brandId ? (
        <CompetitorMatrixView brandId={brandId} brandName={activeBrand?.name || t('competitors.myBrand')} brandDomain={activeBrand?.url} />
      ) : (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('competitors.selectBrand')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
